/**
 * DataGrid Adapter
 * Transforms Product Field Registry into MUI DataGrid column definitions
 */

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Product } from '../../../services/api/domains/products/types';
import { PRODUCT_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import {
  getGridFilterOperatorsForType,
  getAsyncSearchFilterOperators,
} from '../../../utils/fieldOperators';
import { createCellRenderer as createSharedCellRenderer } from '../../../shared/utils/cellRenderers';
import ExpandableCell from '../../../components/ExpandableCell';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Create a cell renderer for a specific Product field
 * Uses shared renderer with custom override for preferred_product_name
 */
function createProductCellRenderer(
  field: BaseFieldDefinition<Product>
): GridColDef['renderCell'] {
  // Custom override for preferred_product_name - show preferred or fallback to product_name
  if (field.key === 'preferred_product_name') {
    return (params: GridRenderCellParams<Product>) => {
      const value =
        params.row.preferred_product_name || params.row.product_name;
      return (
        <ExpandableCell
          content={value}
          isHtml={field.renderConfig?.formatAs === 'html'}
          maxLength={field.renderConfig?.maxDisplayLength || 100}
        />
      );
    };
  }

  // Use shared cell renderer factory for all other fields
  return createSharedCellRenderer<Product>(field);
}

/**
 * Convert field registry to MUI DataGrid column definitions
 * @param fieldKeys - Optional array of field keys to include. If not provided, all fields are included.
 * @returns Array of GridColDef for MUI DataGrid
 */
export function toDataGridColumns(fieldKeys?: string[]): GridColDef<Product>[] {
  const fieldsToRender = fieldKeys
    ? PRODUCT_FIELDS_REGISTRY.filter(f => fieldKeys.includes(f.key as string))
    : PRODUCT_FIELDS_REGISTRY;

  return fieldsToRender.map(field => {
    // Determine filter operators based on data type
    let filterOperators;
    if (field.isFilterable) {
      // For relations with API endpoint, use async search operators (check first)
      if (
        field.isRelation &&
        field.relationConfig?.apiEndpoint &&
        field.relationConfig?.searchField
      ) {
        // Get context-aware config for DataGrid filter context
        // Filter context uses searchField (name) for both value and search
        const contextConfig = getContextAwareConfigFromRegistry(
          PRODUCT_FIELDS_REGISTRY,
          field.key as string,
          'filter'
        );

        if (contextConfig) {
          const asyncConfig = toStandardAsyncSelectConfig(contextConfig);
          filterOperators = getAsyncSearchFilterOperators(
            asyncConfig.apiEndpoint,
            asyncConfig.searchField,
            asyncConfig.valueField, // Uses name field for filter value
            asyncConfig.labelField
          );
        }
      }
      // Check if field has selection options (dropdown/select field)
      // Note: Relational fields may have selectionOptions for status, but we prioritize async search
      else if (field.selectionOptions && field.selectionOptions.length > 0) {
        filterOperators = getGridFilterOperatorsForType(
          'selection',
          field.selectionOptions
        );
      }
      // For arrays and other relations, use string operators for filtering
      else if (field.dataType === 'array' || field.isRelation) {
        filterOperators = getGridFilterOperatorsForType('string');
      } else {
        filterOperators = getGridFilterOperatorsForType(
          field.dataType as 'string' | 'number' | 'date'
        );
      }
    }

    // For relational fields, use the searchField for filtering instead of the array field name
    const fieldName =
      field.isRelation && field.relationConfig?.searchField
        ? field.relationConfig.searchField
        : (field.key as string);

    const column: GridColDef<Product> = {
      field: fieldName,
      headerName: field.label,
      width: field.renderConfig?.width,
      flex: field.renderConfig?.flex,
      sortable: field.isSortable ?? false,
      filterable: field.isFilterable ?? false,
      filterOperators,
      renderCell: createProductCellRenderer(field),
    };

    return column;
  });
}

/**
 * Get default visible columns for Products DataGrid
 * Excludes some fields by default for better initial UX
 */
export function getDefaultVisibleColumns(): string[] {
  return [
    'product_name',
    'preferred_product_name',
    'grades',
    'therapeutic_areas',
    'cas_number',
    'manufacturers',
    'facilities',
    'note',
    'created_date',
    'modified_date',
  ];
}

/**
 * Get all available column keys
 */
export function getAllColumnKeys(): string[] {
  return PRODUCT_FIELDS_REGISTRY.map(f => f.key as string);
}
